'use client';
/**
 * LE CALENDRIER DU LABORATOIRE (RQ, 12/10/2026).
 *
 * « Il s'appellera Calendrier et regroupera tous les événements du labo
 * (planning, conventions, RDD, CQE, dates limites…). »
 *
 * ⚠️ UN SEUL CALENDRIER. Cet écran ne dessine plus sa propre grille : il
 * affiche, en pleine page, celui du tableau de bord (`LabCalendar`), qui
 * agrège toutes les couches — mon planning, conventions et événements des
 * contacts, jours fériés, échéances qualité (CQ, EEQ, péremptions),
 * maintenances, métrologie, actions, audits, revues de documents, revues de
 * direction, revues de contrat. Deux grilles auraient fini par ne plus montrer
 * les mêmes choses.
 *
 * Les conventions et événements se GÈRENT depuis la fiche du contact (carnet
 * de contacts) : la liste ci-dessous n'en est que la légende.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { couleurDe, type ConventionVue } from '@/lib/conventions';
import { card, TitrePage } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { LabCalendar } from '@/components/screens/Dashboard';

export default function Calendrier() {
  const { go } = useGed();
  const [conventions, setConventions] = React.useState<ConventionVue[] | null>(null);
  const [erreur, setErreur] = React.useState(false);

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/conventions', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { conventions?: ConventionVue[] }) => { if (vivant) setConventions(d.conventions || []); })
      .catch(() => { if (vivant) { setErreur(true); setConventions([]); } });
    return () => { vivant = false; };
  }, []);

  return (
    <section style={{ display: 'grid', gap: 16 }}>
      <TitrePage titre="Calendrier du laboratoire"
        sousTitre="Tous les événements du laboratoire au même endroit. Le filtre (à droite de la barre) choisit les couches affichées ; un clic sur un événement ouvre sa fiche." />

      <LabCalendar plein />

      <div style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Conventions et événements des contacts</h3>
          <span style={{ flex: 1 }} />
          <Bouton taille="sm" type="button" onClick={() => go('annuaire')}>Gérer depuis l’annuaire →</Bouton>
        </div>
        {conventions === null && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Lecture…</span>}
        {erreur && <span role="alert" style={{ fontSize: 13, color: 'var(--red-600)' }}>La liste n’a pas pu être lue : elle n’est pas vide pour autant.</span>}
        {conventions && !erreur && !conventions.length && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Aucune convention ni aucun événement enregistré. Ils s’ajoutent depuis la fiche d’un contact.</span>}
        {(conventions || []).map((c) => {
          const col = couleurDe(c.couleur);
          return (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', opacity: c.actif ? 1 : 0.55 }}>
              <span style={{ width: 12, height: 12, borderRadius: 'var(--radius-xs)', background: col.fond, border: `2px solid ${col.texte}` }} aria-hidden />
              <strong style={{ fontSize: 13.5 }}>{c.nom}</strong>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.nature === 'evenement' ? 'Événement' : 'Convention'}</span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{c.description}{c.actif ? '' : ' — désactivé(e)'}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
